<template>
  <div style="height: 100%; position: relative">
    <!-- 🔹 Close Button -->
    <button v-if="isPpt" class="close-btn" @click="closePptVideo">
      Close
    </button>
    <div class="video-js-container">
      <div class="video-js vjs-default-skin" :style="componentStyle">
        <div id="video-player-container">
          <video
            id="videoId"
            ref="videoPlayerRef"
            controls
            playsinline
            poster=""
            preload="auto"
          ></video>
        </div>
      </div>

      <Transition name="bounce">
        <MainModal
          v-if="currentQuiz"
          :selectedQuiz="currentQuiz"
          @clearSelectedQuizFromChild="currentQuiz = null"
          :responseContentList="responseContentList"
          :maxStudentCount="maxStudentCount"
          :handleModalClose="handleModalClose"
        />
      </Transition>
    </div>
  </div>
</template>


<script setup>
import {ref, onMounted, onBeforeUnmount, computed, watch, onUnmounted} from 'vue'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import 'videojs-youtube'
import MainModal from './components/MainModal.vue'
import store from '@/store'
import {throttle} from 'lodash'

const emit = defineEmits(['closeVideoPlayer']) 

const props = defineProps({
  video: {
    type: Object,
  },
  responseContentList: {
    type: Array,
    function() {
      return []
    },
  },
  getResponeCount: Function,
  getPage: {
    type: Object,
    default: {}
  },
  isPpt: {
    type: Boolean,
    default: false
  }
})

let player = null
let currentTooltip = null
let isMarkerDown = false
let startSkipTime = 0
let endSkipTime = 0
let timeRange = null
let changeTime = null
let curStTime = 0
let curEndTime = 0
let curDuration = 0
let playList = []
const isSeekDragging = ref(false)
const currentVideoIndex = ref(0)
const isPlayingBlackScreen = ref(false)
const updateIndex = ref(false)
const loading = ref(false)
const videoPlayerRef = ref(null)
const videoQuizes = ref([])
const startTrim = ref(props.video.trimOptions?.startTime)
const endTrim = ref(props.video.trimOptions?.endTime)
const currentQuiz = ref(null)
const maxStudentCount = ref(0)
const findQuestion = ref(undefined)
const isMarkerClicked = ref(undefined)
const currentVideoTime = computed(() => store.state.teacher.videoCurrentTime.time)
const newVideoIndex = computed(() => store.state.teacher.videoCurrentTime.videoIndex)
const videoPlayPause = computed(() => store.state.teacher.videoPlayPause)
const videoInteractiveMode = computed(() => store.state.teacher.videoInteractiveMode)
const allStudents = computed(() => store.state.teacher.rooms.members)
const onlineTeachers = computed(() => store.state.teacher.onlineTeachers)
const currentQId = computed(() => store.state.student.currentVideoQuizId)
const componentStyle = computed(() => ({
  width: '100%',
  height: 'calc(100vh - 110px)',
}))

const closePptVideo = () => {
  if (player) {
    player.dispose()
  }
  emit('closeVideoPlayer')
}

watch(
  () => allStudents.value,
  (newCount) => {
    maxStudentCount.value = newCount?.length
  }
)

watch(
  onlineTeachers,
  async (newTeachers) => {
    const curSec = player.currentTime()
    await sendVideoCurrentTime({time: curSec, videoIndex: currentVideoIndex.value})
  },
  {deep: true}
)

watch(isMarkerClicked, (newVal) => {
  if (newVal) {
    findQuestion.value = newVal
  } else {
    findQuestion.value = undefined
  }
})

watch(updateIndex, async (newVal) => {
  if (newVal) {
    if (currentVideoIndex.value + 1 < playList.length) {
      currentVideoIndex.value++
      changeTime = null
      changeSource(0, playList)
    }  else {
      updateIndex.value = false
    }
  }
})

const updateMarkers = (videoQuizes) => {
  if (videoQuizes?.length) {
    videoQuizes.forEach((quiz) => {
      if (parseFloat(quiz.page) >= startTrim.value && parseFloat(quiz.page) <= endTrim.value) {
        addMarker(parseFloat(quiz.page), quiz)
      }
    })
  }
}

const handleModalClose = async () => {
  if (currentVideoTime.value > startTrim.value) player.currentTime(currentVideoTime.value + 1.2)
  if (isMarkerClicked.value) {
    const qTime = parseFloat(isMarkerClicked.value.page + curStTime - startSkipTime) + 1.2
    player.currentTime(qTime)
    await sendVideoCurrentTime({time: qTime, videoIndex: currentVideoIndex.value})
  }
  await sendQuizApi(null)
  await sendPlayPause(true)
  currentQuiz.value = undefined
  isMarkerClicked.value = undefined
  const customProgressPlayed = document.getElementById('custom-progress-played')
  customProgressPlayed.classList.add('custom-progress-played')
}

const hideAllTooltips = () => {
  document.querySelectorAll('.vjs-tooltip').forEach((tooltip) => {
    tooltip.style.display = 'none'
  })
}

const initFn = async () => {
  await sendPlayPause()
}

// watch(
//   () => store.state.session,
//   (newVal) => {
//     if (newVal.video && newVal.video.length) {
//       startTrim.value = 0
//       endTrim.value = newVal.video.reduce((prev, cur) => prev + (cur.trimOptions.endTime - cur.trimOptions.startTime + 1), -1)
//       playList = newVal.video
//       videoQuizes.value = updateQuestions(newVal.questions, newVal.video)
//     }
//   },
//   {deep: true}
// )

const getVideoDetails = (playList, currentVideoIndex) => {
  if (!timeRange) {
    setTimeForVideoes(playList)
  }
  startSkipTime = timeRange[currentVideoIndex]
  endSkipTime = timeRange[currentVideoIndex + 1]
  let video = playList[currentVideoIndex]
  const videoId = video.videoId?.length ? video.videoId.split('::')[0] : undefined
  const obj = {}
  if (video.type === 'youtube') {
    obj.type = 'video/youtube'
    obj.src = `https://www.youtube.com/watch?v=${videoId}`
  } else {
    obj.type = 'video/mp4'
    obj.src = Fn.hashToUrl(videoId)
  }
  obj.startTime = video.trimOptions.startTime + startSkipTime
  obj.endTime = video.trimOptions.endTime + startSkipTime
  obj.duration = obj.endTime - obj.startTime
  curStTime = video.trimOptions.startTime
  curEndTime = video.trimOptions.endTime
  curDuration = obj.duration
  return obj
}

function findIndexClosest(timeRange, newTrimmedTime, findSmaller = true) {
  let left = 0
  let right = timeRange.length - 1
  let result = -1

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    if (findSmaller) {
      if (timeRange[mid] < newTrimmedTime) {
        result = mid
        left = mid + 1
      } else {
        right = mid - 1
      }
    } else {
      if (timeRange[mid] > newTrimmedTime) {
        result = mid
        right = mid - 1
      } else {
        left = mid + 1
      }
    }
  }

  return result
}

const updateCustomTimeDisplay = (time, duration) => {
  if (document.getElementById('custom-current-time')) {
    document.getElementById('custom-current-time').textContent = secondToTimeFormat(time)
  }
  if (duration && document.getElementById('custom-duration')) {
    document.getElementById('custom-duration').textContent = secondToTimeFormat(duration)
  }
  const percentage = (time / (duration || player.duration())) * 100
  const customProgressPlayed = document.getElementById('custom-progress-played')
  if (customProgressPlayed) {
    customProgressPlayed.style.width = `${percentage}%`
  }
}

function recreateVideoElement() {
  if (player) {
    player.off()
    player.dispose()
    player = null
  }

  const container = document.getElementById('video-player-container')
  if (!container) {
    return
  }

  container.innerHTML = `
    <video id="videoId" ref="videoPlayerRef" controls playsinline></video>
  `
  videoPlayerRef.value = document.getElementById('videoId')
}

let isChanged = false
function changeSource(relativeTime = 0, playList, noBlack = false) {
  loading.value = true

  isPlayingBlackScreen.value = false
  let isMuted = player.muted()

  const {type, src} = getVideoDetails(playList, currentVideoIndex.value)
  recreateVideoElement()
  player = getPlayer(playList[currentVideoIndex.value], videoPlayerRef, false, isMuted)
  isChanged = true
  readyPlayer(false)

  player.load()
  player.one('loadedmetadata', async () => {
    if(!currentQId.value){
      player.play().catch((e) => {
      })
      await sendPlayPause(true)
    }
    player.one('canplay', async () => {
      player.currentTime(changeTime || curStTime)
      await sendVideoCurrentTime({time: changeTime || curStTime, videoIndex: currentVideoIndex.value})
      // player.play()
    })
  })
  // player.src({type, src})
  updateCustomTimeDisplay(startSkipTime, timeRange.at(-1))
  isSeekDragging.value = false
  updateIndex.value = false
  loading.value = false
}

let videoChange = false
const readyPlayer = (init = true) => {

  player.ready(async () => {
    initFn()

    const observer = new MutationObserver((mutations, obs) => {
      const controlBar = player.el().querySelector('.vjs-control-bar')
      if (controlBar) {
        obs.disconnect()

        if(player && videoChange){
          setTimeout(async () => {
            player.pause()
          }, 900)
          videoChange = false
        }
      }
    })
    observer.observe(player.el(), { childList: true, subtree: true })

    player.on('play', async () => {
      if (!currentQuiz.value) {
        player.play()
        await sendPlayPause(true)
      }
    })
    player.on('pause', async () => {
      if (!currentQuiz.value) {
        await sendPlayPause(false)
      }
    })
    player.on('loadedmetadata', () => {
      player.duration = function () {
        return curDuration
      }
      player.currentTime(changeTime || curStTime)
      updateMarkers(videoQuizes.value)
      player.on('timeupdate', handleTimeUpdate)
    })
    player.poster('')
    player.controlBar.el().addEventListener('click', (e) => {
      if (e.target.closest('.vjs-marker')) return
      hideAllTooltips()
    })
    const customTimeDuration = videojs.dom.createEl('div', {
      id: 'custom-time-display',
      className: 'custom-time-display',
      innerHTML: `<span id="custom-current-time">-</span> / <span id="custom-duration">-</span>`,
    })
    player.controlBar.el().appendChild(customTimeDuration)
    const interactiveButton = videojs.dom.createEl('div', {
      id: 'interactive-button',
      className: 'interactive-button',
      innerHTML: `
      <span class="span-text">Interactive mode</span>
          <label class="switch">
              <input type="checkbox" id="toggle-checkbox">
              <span class="slider round"></span>
              </label>
      `,
    })
    const checkbox = interactiveButton.querySelector('#toggle-checkbox')
    checkbox.checked = videoInteractiveMode.value
    function getMarkers(value) {
      const CurrentMarkers = document.getElementsByClassName('vjs-marker')
      Array.from(CurrentMarkers).forEach((curMarker) => {
        curMarker.style.display = value ? 'block' : 'none'
      })
    }

   if(init){
       watch(videoInteractiveMode, (newValue) => {
        checkbox.checked = newValue
        getMarkers(newValue)
      })

      watch(
        () => currentVideoTime.value,
        (newVal, oldVal) => {
          if (player) {
            if (newVideoIndex.value === currentVideoIndex.value) {
              if (newVal === 0) {
                player.currentTime(oldVal)
                return
              } else {
                player.currentTime(newVal)
              }
            } else {
              changeTime = newVal ? newVal : oldVal
              currentVideoIndex.value = newVideoIndex.value
              changeSource(0, playList, true)
            }
          }
        }
      )

      watch(currentQId, async (newVal) => {
        if (!newVal) {
          player.play()
          await sendPlayPause(true)
          currentQuiz.value = undefined
          isMarkerDown = false
          const customProgressPlayed = document.getElementById('custom-progress-played')
          customProgressPlayed.classList.add('custom-progress-played')
        } else {
          if (!findQuestion.value) {
            const quiz = videoQuizes.value.find((item) => item._id === newVal)
            isMarkerDown = true
            player.pause()
            player.currentTime(parseFloat(quiz?.page))
            findQuestion.value = quiz
          }
        }
      })

      watch(currentQuiz, async (newVal) => {
        if (newVal) {
          player.pause()
          await sendQuizApi(newVal?._id)
        } else {
          await sendQuizApi(null)
        }
      })

      watch(videoPlayPause, async (newVal, oldVal) => {
        if (newVal) {
          const nxtSec = player.currentTime() + 1
          player.play()
          if (nxtSec <= startTrim.value + 1) return
          await sendVideoCurrentTime({time: nxtSec, videoIndex: currentVideoIndex.value})
        } else {
          const curSec = player.currentTime()
          player.pause()
          if (curSec <= startTrim.value) return
          await sendVideoCurrentTime({time: curSec, videoIndex: currentVideoIndex.value})
        }
      })

      watch(findQuestion, async (newValue) => {
        const customProgressPlayed = document.getElementById('custom-progress-played')
        if (newValue) {
          player.pause()
          await sendPlayPause(false)
          currentQuiz.value = newValue
          customProgressPlayed.classList.remove('custom-progress-played')
        } else {
          customProgressPlayed.classList.add('custom-progress-played')
          if (isMarkerDown) {
            player.play()
            await sendPlayPause(true)
          }
          isMarkerDown = false
        }
      })
   }

    player.controlBar.el().appendChild(interactiveButton)
    document.getElementById('toggle-checkbox').addEventListener('change', function () {
      videoInteractiveMode.value = this.checked
      sendInteractiveMode(this.checked)
    })
    const customSeekBar = videojs.dom.createEl('div', {
      id: 'custom-progress-container',
      className: 'custom-progress-container',
      innerHTML: `<div id="custom-progress-played" class="custom-progress-played"></div>
      <div id="custom-progress-buffered" class="custom-progress-buffered"></div>`,
    })

    let isSeekUpdating = false
    let savedTimeUpdateListener = null
    const newActualTime = ref(0)
    watch(newActualTime, async (newVal) => {
      player.currentTime(newVal)
      await sendVideoCurrentTime({time: newActualTime.value, videoIndex: currentVideoIndex.value})
    })

    const updateTime = (event) => {
      if (isSeekUpdating || loading.value) return // Prevent overlapping updates
      isSeekUpdating = true

      const rect = customSeekBar.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const width = rect.width
      if (!width) return
      const percent = Math.max(0, Math.min(1, clickX / width))
      const trimmedDuration = endTrim.value - startTrim.value
      const newTrimmedTime = percent * trimmedDuration
      if (newTrimmedTime < startSkipTime) {
        const idx = findIndexClosest(timeRange, newTrimmedTime)
        startSkipTime = timeRange[idx] || 0
        endSkipTime = timeRange[idx + 1]
        currentVideoIndex.value = idx
        // const {type, src} = getVideoDetails(playList, idx)
        loading.value = true
        changeSource(0, playList, true)
        changeTime = curStTime + newTrimmedTime - startSkipTime
        videoChange = true
        loading.value = false
      } else if (newTrimmedTime >= endSkipTime) {
        const idx = findIndexClosest(timeRange, newTrimmedTime)
        startSkipTime = timeRange[idx] || 0
        endSkipTime = timeRange[idx + 1] || endTrim.value
        currentVideoIndex.value = idx
        loading.value = true
        changeSource(0, playList, true)
        videoChange = true
        changeTime = curStTime + newTrimmedTime - startSkipTime
        loading.value = false
      }
      newActualTime.value = curStTime + newTrimmedTime - startSkipTime
      if (isSeekDragging.value) {
        changeTime = newActualTime.value
      }
      if (document.getElementById('custom-current-time')) document.getElementById('custom-current-time').textContent = secondToTimeFormat(newTrimmedTime)
      if (document.getElementById('custom-duration')?.textContent === '-') {
        document.getElementById('custom-duration').textContent = secondToTimeFormat(timeRange.at(-1))
      }
      const playedBar = customSeekBar.querySelector('#custom-progress-played')
      playedBar.style.width = `${percent * 100}%`
      isSeekUpdating = false
    }
    const pauseTimeUpdateEvents = () => {
      savedTimeUpdateListener = onTimeUpdate
      player.off('timeupdate', onTimeUpdate)
    }
    const resumeTimeUpdateEvents = () => {
      if (savedTimeUpdateListener) {
        player.on('timeupdate', savedTimeUpdateListener)
        savedTimeUpdateListener = null
      }
    }
    const onTimeUpdate = () => {
      if (!isSeekDragging.value && !isSeekUpdating && !loading.value) {
        const currentTime = player.currentTime() + startSkipTime
        const trimmedDuration = endTrim.value - startTrim.value
        const percent = ((currentTime - curStTime) / trimmedDuration) * 100
        if (percent < 0) return
        throttledProgressUpdate(percent)
      }
    }

    customSeekBar.addEventListener('click', (event) => {
      updateTime(event)
      let cTimeStr = document.getElementById('custom-current-time')?.textContent
      if (cTimeStr) {
        let timeParts = cTimeStr.split(':').map(Number)
        let curTime = timeParts[0] * 60 + timeParts[1]
        // player.currentTime(curTime)
      }
      if (!isMarkerClicked.value) {
        player.play().catch((e) => {
        })
      }
    })
    customSeekBar.addEventListener('mousedown', (event) => {
      if (isMarkerClicked.value && isMarkerClicked.value.page < curEndTime) return
      event.preventDefault()
      isSeekDragging.value = true
      pauseTimeUpdateEvents()
      player.play()
      updateTime(event)
    })
    document.addEventListener('mousemove', (event) => {
      if (isSeekDragging.value) {
        updateTime(event)
      }
    })
    document.addEventListener('mouseup', () => {
      if (isChanged) {
        setTimeout(() => {
          player.currentTime(changeTime)
          player.pause()
          isSeekDragging.value = false
          resumeTimeUpdateEvents()
          isChanged = false
        }, 100)
      } else {
        if (isSeekDragging.value) {
          isSeekDragging.value = false
          // player.currentTime(newActualTime.value)
          updateTime(event)
          resumeTimeUpdateEvents()
          player.pause()
          // await sendVideoCurrentTime(newActualTime.value)
        }
      }
    })
    player.on('timeupdate', onTimeUpdate)
    player.controlBar.el().appendChild(customSeekBar)
  })
}

function setTimeForVideoes(playList) {
  let sumTime = 0
  const timeArr = [0]
  for (let i = 0; i < playList.length; i++) {
    sumTime += playList[i].trimOptions.endTime - playList[i].trimOptions.startTime + 1
    timeArr.push(sumTime)
  }
  timeRange = timeArr
  return timeArr
}

const updateQuestions = (questions = [], playList = []) => {
  const timeRange = setTimeForVideoes(playList)

  const updatedQuestions = []

  for (let i = 0; i < playList.length; i++) {
    const video = playList[i]
    const { startTime, endTime } = video.trimOptions
    const sTime = timeRange[i]
    
    const relatedQs = questions.filter(q => q.id === video.videoId)

    for (const q of relatedQs) {
      if (q.page >= startTime && q.page <= endTime) {
        updatedQuestions.push({
          ...q,
          page: q.page - startTime + sTime,
        })
      }
    }
  }
  return updatedQuestions
}

const getPlayer = (videoData, videoPlayerRef, allowPlay = false, muted = true) => {
  const videoId = videoData.videoId?.length ? videoData.videoId.split('::')[0] : undefined
  if (videoData.type === 'youtube') {
    player = videojs(videoPlayerRef.value, {
      techOrder: ['youtube'],
      fluid: false,
      controlBar: {
        volumePanel: {
          inline: false,
        },
        progressControl: {
          seekBar: {
            mouseTimeDisplay: false,
          },
        },
        children: ['playToggle', 'volumePanel'],
      },
      preload: 'auto',
      autoplay: allowPlay,
      loadingSpinner: false,
      muted: muted,
      bigPlayButton: false,
      poster: '',
      sources: [
        {
          type: 'video/youtube',
          src: `https://www.youtube.com/watch?v=${videoId}`,
        },
      ],
      youtube: {
        ytControls: 0,
      },
    })
  } else {
    player = videojs(videoPlayerRef.value, {
      techOrder: ['html5'],
      controlBar: {
        volumePanel: {
          inline: false,
        },
        progressControl: {
          seekBar: {
            mouseTimeDisplay: false,
          },
        },
        children: ['playToggle', 'volumePanel'],
      },
      preload: 'auto',
      autoplay: allowPlay,
      muted: muted,
      inactivityTimeout: 0,
      bigPlayButton: false,
      sources: [
        {
          type: 'video/mp4',
          src: Fn.hashToUrl(videoId),
        },
        {
          type: 'video/webm',
          src: Fn.hashToUrl(videoId, 'webm'), // e.g. "https://example.com/video.webm"
        },
        {
          type: 'video/ogg',
          src: Fn.hashToUrl(videoId, 'ogv'), // e.g. "https://example.com/video.ogv"
        },
      ],
    })
  }
  curStTime = videoData.trimOptions.startTime
  curEndTime = videoData.trimOptions.endTime
  curDuration = curEndTime - curStTime
  return player
}

onMounted(async () => {
  if(Array.isArray(store.state.session.video)){
    playList = store.state.session.video
  } else if(props.getPage?.type === 'video' && props.video) {
    playList = [props.video]
  } else {
    playList = [store.state.session.video]
  }
  startTrim.value = 0
  endTrim.value = playList.reduce((prev, cur) => prev + (cur.trimOptions.endTime - cur.trimOptions.startTime + 1), -1)
  videoQuizes.value = updateQuestions(store.state.session.questions, playList)
  maxStudentCount.value = allStudents.value?.length
  player = getPlayer(props.video || playList[0], videoPlayerRef)
  readyPlayer()
  if(!timeRange){
    setTimeForVideoes(playList)
  }
  endSkipTime = timeRange[1]
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

function handleBeforeUnload(event) {
  if (player) {
    const curSec = player.currentTime()
    if (curSec > startTrim.value) sendVideoCurrentTime({time: curSec, videoIndex: currentVideoIndex.value})
    player.pause()
    player.dispose()
  }
}

onUnmounted(() => {
  if (player) {
    const curSec = player.currentTime()
    if (curSec <= startTrim.value) return
    sendVideoCurrentTime({time: curSec, videoIndex: currentVideoIndex.value})
    player.dispose()
  }
})


const throttledProgressUpdate = throttle((progressPercent) => {
  const customProgressPlayed = document.getElementById('custom-progress-played')
  if (customProgressPlayed) customProgressPlayed.style.width = `${progressPercent}%`
}, 100)

const sendVideoCurrentTime = throttle(async ({time, videoIndex}) => {
  try {
    const response = await App.service('auth').patch('syncTeacher', {
      commit: 'teacher/setVideoCurrentTime',
      data: {time, videoIndex},
    })
    store.dispatch('teacher/setVideoCurrentTime', response.data)
  } catch (error) {
  }
}, 1000)

const sendPlayPause = throttle(async (isPlay) => {
  try {
    const response = await App.service('auth').patch('syncTeacher', {
      commit: 'teacher/setVideoPlayPause',
      data: isPlay,
    })
    store.dispatch('teacher/setVideoPlayPause', response.data)
  } catch (error) {
  }
}, 100)

const sendQuizApi = throttle(async (id) => {
  await App.service('auth').patch('syncTeacher', {commit: 'student/setCurrentVideoQuizId', data: id})
  store.dispatch('student/setCurrentVideoQuizId', id)
}, 100)

const sendInteractiveMode = async (mode) => {
  try {
    const response = await App.service('auth').patch('syncTeacher', {
      commit: 'teacher/setVideoInteractiveMode',
      data: mode,
    })
    store.dispatch('teacher/setVideoInteractiveMode', response.data)
  } catch (error) {
  }
}

const handleTimeUpdate = async () => {
  if (currentQuiz.value || !player.currentTime() || isSeekDragging.value || isMarkerDown || updateIndex.value) return

  const currentTime = player.currentTime()
  const trimmedDuration = endTrim.value - startTrim.value
  let trimmedCurrentTime = currentTime - startTrim.value + startSkipTime
  if (!updateIndex.value && (Math.abs(currentTime - curEndTime) < 1 || currentTime > curEndTime)) {
    player.pause()
    updateIndex.value = true
  }
  trimmedCurrentTime -= curStTime
  if (trimmedCurrentTime < 0) return

  if (currentTime < curStTime || currentTime > curEndTime) {
    return
  }
  const progressPercent = (trimmedCurrentTime / trimmedDuration) * 100
  throttledProgressUpdate(progressPercent)
  if (document.getElementById('custom-current-time')) document.getElementById('custom-current-time').textContent = secondToTimeFormat(trimmedCurrentTime)
  if (document.getElementById('custom-duration')) document.getElementById('custom-duration').textContent = secondToTimeFormat(trimmedDuration)
  if (videoQuizes.value?.length && videoInteractiveMode.value) {
    findQuestion.value = videoQuizes.value.find((o) => {
      const markerTime = parseFloat(o.page) - startTrim.value
      const markerTolerance = 1
      return trimmedCurrentTime < markerTime && Math.abs(trimmedCurrentTime - markerTime) <= markerTolerance
    })
  }
}

const addMarker = (time, quiz) => {
  const customProgressContainer = document.getElementById('custom-progress-container')
  if (customProgressContainer) {
    const adjustedTime = time - startTrim.value
    const trimmedDuration = endTrim.value - startTrim.value
    const percentage = (adjustedTime / trimmedDuration) * 100
    const marker = videojs.dom.createEl('div', {
      className: 'vjs-marker',
      style: `
      left: calc(${percentage}% - 6px);
      display: ${videoInteractiveMode.value ? 'block' : 'none'};
    `,
      id: `marker-${quiz._id}`,
    })
    const tooltip = videojs.dom.createEl('div', {
      className: 'vjs-tooltip',
      style: 'display: none;',
      innerHTML: `
        <div class="tooltip-header">
          <div class="quiz-type">${quiz.type === 'choice' ? 'Multiple Choice Question' : 'Open Ended Question'}</div>
          <div class="close-icon" style="cursor: pointer; color: #FFF;">
            &#10006;
          </div>
        </div>
        <div class="tooltip-content">
          <div class="quiz-text">${quiz.data.length > 10 ? quiz.data.slice(0, 10) + '...' : quiz.data}</div>
          <div class="time-edit">
            <div class="time">${secondToTimeFormat(adjustedTime)}</div>
          </div>
        </div>
      `,
    })

    tooltip.querySelector('.close-icon').addEventListener('click', (e) => {
      e.stopPropagation()
      tooltip.style.display = 'none'
    })

    marker.appendChild(tooltip)

    tooltip.addEventListener('mousedown', (e) => {
      e.stopPropagation()
    })

    marker.addEventListener('mouseover', () => {
      if (currentTooltip && currentTooltip !== tooltip) {
        currentTooltip.style.display = 'none'
      }
      tooltip.style.display = 'block'
      currentTooltip = tooltip
    })

    marker.addEventListener('mousedown', async () => {
      isMarkerClicked.value = quiz
      await sendPlayPause(false)
    })

    customProgressContainer.appendChild(marker)
  }
}

function secondToTimeFormat(seconds) {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${minutes}:${secs < 10 ? '0' : ''}${secs}`
}
</script>

<style scoped>
.default-tab {
  color: black;
}

.active-tab {
  color: var(--q-color-primary);
  font-weight: bold;
}

.custom-outline {
  --q-btn-outline-color: black;
}

.no-uppercase {
  text-transform: none;
}

::v-deep(.close-icon) {
  top: 20px;
}

.video-js-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

::v-deep(.vjs-poster) {
  display: none !important;
}

.video-js-container .video-js .vjs-tech {
  border-radius: inherit;
  background: none !important;
}

.vjs-youtube.vjs-user-inactive .vjs-iframe-blocker {
  display: none !important;
}

.video-js-container .vjs-control-bar {
  display: flex;
  position: inherit;
  background-color: transparent;
  position: absolute;
  bottom: 0px;
  z-index: 1;
  background: #000;
  justify-content: center;
}

::v-deep(.video-js .vjs-control-bar) {
  justify-content: center;
  align-items: center;
}

::v-deep(.vjs-play-progress) {
  background-color: #26a69a;
}

::v-deep(.vjs-play-progress):before {
  top: -1px;
  font-size: 1.5em;
}

::v-deep(.vjs-play-progress).vjs-play-progress-overlapping:before {
  color: #26a69a;
}

::v-deep(.vjs-marker) {
  width: 12px !important;
  height: 12px !important;
  border-radius: 50% !important;
  background-color: #26a69a !important;
  position: absolute;
  top: -3px;
  bottom: 0;
}

::v-deep(.vjs-marker) .vjs-marker-content {
  background-color: #26a69a;
  height: 100%;
  width: 100%;
}

::v-deep(.vjs-tooltip) {
  position: absolute;
  background-color: #313033;
  color: white;
  width: 300px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  cursor: auto;
}

::v-deep(.vjs-tooltip) .tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.quiz-type {
  width: 100%;
  text-align: center;
  font-size: 12px;
}

::v-deep(.vjs-tooltip) .tooltip-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-size: 18px;
}

::v-deep(.vjs-tooltip) .time-edit {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  width: 100%;
}

@media screen and (max-width: 580px) {
  ::v-deep(.vjs-tooltip) {
    width: 200px;
    padding: 10px;
    border-radius: 5px;
    top: -110px;
    font-size: 12px;
  }

  .quiz-type {
    font-size: 12px;
  }

  ::v-deep(.quiz-text) {
    font-size: 0.95rem;
  }

  ::v-deep(.vjs-tooltip) .time-edit {
    font-size: 12px;
  }
}

.custom-mcq-icon {
  background-color: #f6993f;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0.3em;
  color: #fff;
}

.custom-text-icon {
  background-color: #e3342f;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0.3em;
  color: #fff;
}

.quiz-type {
  width: 100%;
  text-align: center;
  font-size: 12px;
}

::v-deep(.vjs-progress-control) {
  display: none;
}

::v-deep(.vjs-play-progress) {
  background-color: #26a69a;
}

::v-deep(.vjs-play-progress):before {
  top: -1px;
  font-size: 1.9em;
}

::v-deep(.custom-progress-container) {
  position: relative;
  width: 640px;
  height: 5px;
  background-color: #c4c2c250;
  cursor: pointer;
  margin-right: 20px;
}

::v-deep(.custom-progress-played) {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: #26a69a;
  width: 0%;
}

::v-deep(.custom-progress-played)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(50%, -50%);
  width: 14px;
  height: 14px;
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
  z-index: 99;
}

::v-deep(.vjs-custom-progress-bar) {
  width: 100%;
  height: 5px;
  background-color: rgba(255, 255, 255, 0.3);
  position: absolute;
  bottom: 10px;
  left: 0;
}

::v-deep(.vjs-custom-progress-bar) .vjs-progress-bar-inner {
  height: 100%;
  background-color: #ff0000;
  width: 0;
  transition: width 0.1s linear;
}

::v-deep(.custom-progress-buffered) {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: #26a69a;
  width: 0%;
}

::v-deep(.custom-time-display) {
  font-size: 11px;
  color: #fff;
  margin-right: 10px;
  display: flex;
  align-items: center;
}

::v-deep(.custom-time-display) span {
  margin: 0 2px;
}

::v-deep(.interactive-button) {
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 3px;
  gap: 0.5rem;
}

::v-deep(.interactive-button .span-text) {
  font-size: small;
}

::v-deep(.switch) {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 25px;
  margin-right: 0.5rem;
}

::v-deep(.switch input) {
  opacity: 0;
  width: 0;
  height: 0;
}

::v-deep(.slider) {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

::v-deep(.slider:before) {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

::v-deep(input:checked + .slider) {
  background-color: #26a69a;
}

::v-deep(input:focus + .slider) {
  box-shadow: 0 0 1px #26a69a;
}

::v-deep(input:checked + .slider:before) {
  -webkit-transform: translateX(16px);
  -ms-transform: translateX(16px);
  transform: translateX(16px);
}

::v-deep(.slider.round) {
  border-radius: 20px;
}

::v-deep(.slider.round:before) {
  border-radius: 50%;
}

@media screen and (max-width: 1050px) {
  ::v-deep(.slider:before) {
    height: 16px;
    width: 16px;
    bottom: 4.5px;
  }
}

.bounce-enter-active {
  animation: bounce-in 0.5s;
}

.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.25);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #fff;
  color: #000;
  border: none;
  border-radius: 10px;
  font-size: 12px !important;
  padding: 0 20px;
  height: 32px;
  font-size: 18px;
  line-height: 32px;
  cursor: pointer;
  z-index: 1000;
  transition: background 0.2s ease;
}
</style>
